import { useCallback, useEffect, useRef, useState } from 'react';
import {
  acceptIncomingQrOrder,
  declineIncomingQrOrder,
} from '../utils/restaurantIncomingOrderActions';
import {
  getIncomingOpenOrders,
  getIncomingOrderSignature,
  getDismissedQrPopupOrderIds,
  markQrPopupOrdersDismissed,
  playIncomingQrOrderAlertSound,
  setGlobalQrOrderNotifierActive,
  snapToIncomingPrepOption,
} from '../utils/restaurantQrIncomingOrders';
import {
  runRestaurantOrdersPoll,
  subscribeRestaurantOrdersPoll,
} from '../utils/restaurantOrdersPollCoordinator';

const EMPTY_DIALOG_STATE = {
  open: false,
  queue: [],
  activeOrder: null,
  processing: false,
};

export function useRestaurantQrOrderNotifications({
  hotelId,
  enabled = true,
} = {}) {
  const [dialogState, setDialogState] = useState(EMPTY_DIALOG_STATE);
  const [prepMinutes, setPrepMinutes] = useState(30);
  const [orders, setOrders] = useState([]);
  const [actionError, setActionError] = useState('');
  const initializedRef = useRef(false);
  const knownSignaturesRef = useRef(new Set());
  const handledOrderIdsRef = useRef(new Set());
  const dismissedPopupOrderIdsRef = useRef(new Set());
  const ordersRef = useRef([]);

  const syncIncomingQueue = useCallback((rows, { shouldPrompt = false } = {}) => {
    const queueRows = getIncomingOpenOrders(rows).filter((order) => {
      const orderId = Number(order?.id || 0);
      if (!orderId) return false;
      if (handledOrderIdsRef.current.has(orderId)) return false;
      if (dismissedPopupOrderIdsRef.current.has(orderId)) return false;
      return true;
    });

    if (!initializedRef.current) {
      queueRows.forEach((order) => {
        const signature = getIncomingOrderSignature(order);
        if (signature) knownSignaturesRef.current.add(signature);
      });
      initializedRef.current = true;
      return false;
    }

    const unseenOrders = queueRows.filter((order) => {
      const signature = getIncomingOrderSignature(order);
      return signature && !knownSignaturesRef.current.has(signature);
    });

    if (unseenOrders.length) {
      unseenOrders.forEach((order) => {
        const signature = getIncomingOrderSignature(order);
        if (signature) knownSignaturesRef.current.add(signature);
      });
      if (shouldPrompt) {
        playIncomingQrOrderAlertSound();
      }
    }

    let openedDialog = false;
    setDialogState((prev) => {
      const mergedMap = new Map();
      queueRows.forEach((order) => {
        const orderId = Number(order?.id || 0);
        if (orderId) mergedMap.set(orderId, order);
      });

      const mergedQueue = Array.from(mergedMap.values()).sort((first, second) => {
        const firstTs = new Date(first?.created_at || 0).getTime();
        const secondTs = new Date(second?.created_at || 0).getTime();
        return (Number.isFinite(firstTs) ? firstTs : 0) - (Number.isFinite(secondTs) ? secondTs : 0);
      });

      const activeOrderId = Number(prev?.activeOrder?.id || 0);
      const activeOrder = activeOrderId && mergedMap.has(activeOrderId)
        ? mergedMap.get(activeOrderId)
        : mergedQueue[0] || null;

      const shouldOpen = unseenOrders.length > 0
        || (prev.open && Boolean(mergedQueue.length));

      openedDialog = shouldOpen;
      return {
        open: shouldOpen,
        queue: mergedQueue,
        activeOrder,
        processing: prev.processing,
      };
    });

    if (unseenOrders.length && shouldPrompt) {
      const firstUnseen = unseenOrders[0];
      setPrepMinutes(snapToIncomingPrepOption(firstUnseen?.preparation_time_minutes || 30));
    }

    return openedDialog || unseenOrders.length > 0;
  }, []);

  const applyOrdersSnapshot = useCallback((rows, { shouldPrompt = false, changed = true } = {}) => {
    if (!Array.isArray(rows)) return;
    if (changed || !ordersRef.current.length) {
      ordersRef.current = rows;
      setOrders(rows);
    }
    syncIncomingQueue(rows, { shouldPrompt });
  }, [syncIncomingQueue]);

  useEffect(() => {
    setGlobalQrOrderNotifierActive(enabled && Boolean(hotelId));
    return () => setGlobalQrOrderNotifierActive(false);
  }, [enabled, hotelId]);

  useEffect(() => {
    if (!enabled || !hotelId) {
      initializedRef.current = false;
      knownSignaturesRef.current = new Set();
      handledOrderIdsRef.current = new Set();
      dismissedPopupOrderIdsRef.current = new Set();
      ordersRef.current = [];
      setOrders([]);
      setDialogState(EMPTY_DIALOG_STATE);
      return undefined;
    }

    dismissedPopupOrderIdsRef.current = getDismissedQrPopupOrderIds(hotelId);

    const unsubscribe = subscribeRestaurantOrdersPoll(
      (normalizedOrders, meta = {}) => {
        const shouldPrompt = Boolean(meta.changed) && !meta.cached;
        applyOrdersSnapshot(normalizedOrders, {
          shouldPrompt,
          changed: Boolean(meta.changed),
        });
      },
      { hotelId, immediate: true },
    );

    return unsubscribe;
  }, [applyOrdersSnapshot, enabled, hotelId]);

  useEffect(() => {
    const activeOrder = dialogState.activeOrder;
    if (!activeOrder?.id) return;
    setPrepMinutes(snapToIncomingPrepOption(activeOrder.preparation_time_minutes || 30));
  }, [dialogState.activeOrder?.id]);

  const removeOrderFromQueue = useCallback((orderId) => {
    const normalizedOrderId = Number(orderId || 0);
    if (!normalizedOrderId) return;
    handledOrderIdsRef.current.add(normalizedOrderId);
    setDialogState((prev) => {
      const remainingQueue = (Array.isArray(prev.queue) ? prev.queue : [])
        .filter((row) => Number(row?.id || 0) !== normalizedOrderId);
      const nextActive = remainingQueue[0] || null;
      return {
        open: Boolean(nextActive),
        queue: remainingQueue,
        activeOrder: nextActive,
        processing: false,
      };
    });
  }, []);

  const acceptOrder = useCallback(async () => {
    const order = dialogState.activeOrder;
    const orderId = Number(order?.id || 0);
    if (!orderId || dialogState.processing) return false;
    setActionError('');
    setDialogState((prev) => ({ ...prev, processing: true }));
    try {
      await acceptIncomingQrOrder(order, { orders: ordersRef.current, preparationMinutes: prepMinutes });
      removeOrderFromQueue(orderId);
      await runRestaurantOrdersPoll({ force: true, hotelId });
      return true;
    } catch (error) {
      setActionError(error?.userMessage || error?.message || 'Unable to accept order.');
      setDialogState((prev) => ({ ...prev, processing: false }));
      return false;
    }
  }, [dialogState.activeOrder, dialogState.processing, hotelId, prepMinutes, removeOrderFromQueue]);

  const declineOrder = useCallback(async () => {
    const order = dialogState.activeOrder;
    const orderId = Number(order?.id || 0);
    if (!orderId || dialogState.processing) return false;
    setActionError('');
    setDialogState((prev) => ({ ...prev, processing: true }));
    try {
      await declineIncomingQrOrder(order, { preparationMinutes: prepMinutes });
      removeOrderFromQueue(orderId);
      await runRestaurantOrdersPoll({ force: true, hotelId });
      return true;
    } catch (error) {
      setActionError(error?.userMessage || error?.message || 'Unable to decline order.');
      setDialogState((prev) => ({ ...prev, processing: false }));
      return false;
    }
  }, [dialogState.activeOrder, dialogState.processing, hotelId, prepMinutes, removeOrderFromQueue]);

  const closeDialog = useCallback(() => {
    if (dialogState.processing) return;
    const dismissedOrderIds = (dialogState.queue || [])
      .map((order) => Number(order?.id || 0))
      .filter((orderId) => orderId > 0);
    dismissedOrderIds.forEach((orderId) => {
      dismissedPopupOrderIdsRef.current.add(orderId);
    });
    markQrPopupOrdersDismissed(hotelId, dismissedOrderIds);
    setDialogState(EMPTY_DIALOG_STATE);
    setActionError('');
  }, [dialogState.processing, dialogState.queue, hotelId]);

  return {
    dialogState,
    prepMinutes,
    setPrepMinutes,
    acceptOrder,
    declineOrder,
    closeDialog,
    actionError,
    pendingCount: dialogState.queue.length,
  };
}
